import { apiFetch } from './api.js';
import type { SessionAgency } from '../session/types.js';

export interface AgencySettingsInput {
  /** D-14 Item 8.4. Editable from Phase B. */
  name?: string;
  billingEmail?: string;
  country?: string;
  /** Empty string clears it — "no VAT number" is an ordinary answer. */
  vatNumber?: string;
  /** D-16 ruling 2. Empty string clears it, on the VAT number's contract. */
  businessWebsite?: string;
  /**
   * D-16 ruling 12. `true` only, and only from the one-time prompt: there is no
   * un-confirming, because somebody no longer buying for a business should
   * close the account rather than untick a box.
   */
  businessConfirmed?: true;
}

/**
 * Changes the agency's own details (D-1, extended by D-14 Item 8.4).
 *
 * **The agency name joined the three billing fields in Phase B.** It used to be
 * read-only, with "Get in touch and we will sort it out" underneath, which is a
 * support ticket for a text field. It is the customer's own name for
 * themselves; the operator learns about the change from the activity timeline
 * rather than from an inbox.
 */
export function updateAgencySettings(
  agencyId: string,
  input: AgencySettingsInput,
): Promise<{ agency: SessionAgency }> {
  return apiFetch(`/api/agencies/${agencyId}`, { method: 'PATCH', body: input });
}

/* --------------------------------------------- the invite (D-14 Item 3.4) --- */

/**
 * What the set-password page shows before anything is typed.
 *
 * The agency name and nothing else. A token's holder can already finish the
 * account, so this is not a leak; there is simply nothing else that page needs,
 * and serving the email or the credits would put them in a URL somebody might
 * forward.
 */
export function describeInvite(token: string): Promise<{ agencyName: string }> {
  return apiFetch(`/api/invites/${encodeURIComponent(token)}`);
}

/**
 * Sets the password, finishes the account, and signs them in.
 *
 * The session cookies arrive on the response, so the caller refreshes the
 * session and lands inside the portal rather than on a sign-in page it would
 * have to explain.
 */
export function acceptInvite(input: {
  token: string;
  password: string;
  country: string;
  /** C-123. The owner's own name, required by the server. */
  name: string;
  /** D-16 rulings 1 and 2. Both required by the server. */
  businessWebsite: string;
  businessConfirmed: boolean;
}): Promise<{ agency: SessionAgency; user: { id: string; email: string; name: string } }> {
  return apiFetch('/api/invites/accept', { method: 'POST', body: input });
}

/* ------------------------------------------------------- contact (D-9b 16) --- */

export type ContactCategory = 'volume_order' | 'support' | 'feedback';

/**
 * Sends a contact or support message.
 *
 * `notified` comes back so the page can be honest about what happened. The
 * message is stored either way, and "we have it" is true whether or not a
 * notification reached the operator's inbox.
 */
export function sendContactMessage(input: {
  category: ContactCategory;
  fields: Record<string, string>;
  body: string;
}): Promise<{ message: { id: string; createdAt: string }; notified: boolean }> {
  return apiFetch('/api/contact-messages', { method: 'POST', body: input });
}

/* ------------------------------------------- the login email (Item 8.4) --- */

/**
 * Starts a login-email change.
 *
 * **Nothing changes when this resolves.** A verification link goes to the new
 * address and the account moves only when it is clicked; this call is what
 * sends it, and what tells the old address it is happening.
 *
 * It answers the same way whether or not the address is already in use, on
 * purpose: an endpoint that said so would be a way to find out who has an
 * account.
 */
export function startLoginEmailChange(input: {
  newEmail: string;
  newEmailConfirm: string;
}): Promise<{ started: boolean }> {
  return apiFetch('/api/me/login-email', { method: 'POST', body: input });
}

